import { Controller } from "@hotwired/stimulus"
import touchEventDelegation from "../lib/touch_event_delegation.js"
import dragSelectionManager from "../lib/drag_selection_manager.js"
import { buildInputProof } from "../lib/input_intent_tracker.js"

export default class extends Controller {
    static identifier = "tile"
    static values = {
        workerId: Number,
        tileId: Number,
    }

    connect() {
        // Initialize managers on first tile connection
        if (!this.constructor.managersInitialized) {
            touchEventDelegation.initialize();
            dragSelectionManager.initialize();
            this.constructor.managersInitialized = true;
        }

        this.markTileIfSelfIsQueued()
        this.syncQueueCounterFromDOM()

        if (!this.constructor.tileCount) {
            this.constructor.tileCount = 0;
            this.constructor.turboFrameLoadHandler = (event) => {
                if (event.target.id !== 'grid-panel') return;
                dragSelectionManager.resetDragState();
                if (window.gameUpdatesChannel && typeof window.gameUpdatesChannel.updateQueueCounterFromDOM === 'function') {
                    window.gameUpdatesChannel.updateQueueCounterFromDOM();
                }
            };
            document.addEventListener('turbo:frame-load', this.constructor.turboFrameLoadHandler);
        }
        this.constructor.tileCount++;
    }

    disconnect() {
        this.constructor.tileCount = Math.max(0, (this.constructor.tileCount || 1) - 1);
        if (this.constructor.tileCount === 0) {
            document.removeEventListener('turbo:frame-load', this.constructor.turboFrameLoadHandler);
            this.constructor.turboFrameLoadHandler = null;
        }
    }

    markTileIfSelfIsQueued() {
        const currentWorkerId = parseInt(document.body.dataset.currentWorkerId, 10)

        const queuedMarkers = this.element.querySelectorAll(".mining-presence")
        const isQueued = Array.from(queuedMarkers).some(el =>
            parseInt(el.dataset.workerId, 10) === currentWorkerId
        )

        if (isQueued) {
            const tileInner = this.element.querySelector(`#tile_inner_${this.tileIdValue}`);
            if (tileInner) {
                tileInner.classList.add("you-are-queued-here")
            }
        }
    }

    startMining(event) {
        // Only prevent default for non-touch events (click, mousedown)
        // Touch events come through passive delegation, so preventDefault won't work anyway
        // CSS touch-action: none already handles preventing scroll for touch events
        if (event.type !== 'touchstart' && event.type !== 'touchend' && typeof event.preventDefault === 'function') {
            if (event.type === 'mousedown' && dragSelectionManager.isQueueModeEnabled()) {
                event.preventDefault();
            } else if (event.type === 'click') {
                event.preventDefault();
            }
        }

        const tileId = this.tileIdValue;
        const coords = dragSelectionManager.getEventCoordinates(event);

        // In non-queue mode, skip actions on already queued tiles
        const tileInner = this.element.querySelector(`#tile_inner_${tileId}`);
        const isQueuedTile = tileInner && tileInner.classList.contains('you-are-queued-here');
        if (isQueuedTile && !dragSelectionManager.isQueueModeEnabled()) {
            return;
        }

        // Handle queue mode logic
        if (dragSelectionManager.isQueueModeEnabled()) {
            // For touchstart, set up potential drag
            if (event.type === 'touchstart') {
                dragSelectionManager.startDrag(this.element, coords);
                return;
            }

            // For touchend, check if this was a tap or end of drag
            if (event.type === 'touchend') {
                const touchDuration = Date.now() - dragSelectionManager.touchStartTime;
                const touchDistance = Math.sqrt(
                    Math.pow(coords.x - dragSelectionManager.touchStartPosition.x, 2) + 
                    Math.pow(coords.y - dragSelectionManager.touchStartPosition.y, 2)
                );

                // If it was a quick tap (< 300ms, < 15px movement), treat as single tap
                if (touchDuration < 300 && touchDistance < 15) {
                    dragSelectionManager.cancelDrag();
                    
                    // Process as queue action for single tap
                    if (window.WorkerChannel) {
                        WorkerChannel.performAction("dig", {
                            tile_id: tileId,
                            input_proof: buildInputProof(event, {
                                kind: "click",
                                durationMs: touchDuration,
                                distancePx: touchDistance
                            })
                        });
                    }
                    return;
                }
                // Otherwise, let drag system handle it
                return;
            }

            // For mousedown, prepare for potential drag (desktop)
            if (event.type === 'mousedown') {
                dragSelectionManager.startDrag(this.element, coords);
                return;
            }

            // For click events in queue mode, cancel any pending drag and process as queue action (desktop)
            if (event.type === 'click') {
                dragSelectionManager.cancelDrag();
                this.addClickFeedback();

                // Process as queue action for single click
                if (window.WorkerChannel) {
                    WorkerChannel.performAction("dig", {
                        tile_id: tileId,
                        input_proof: buildInputProof(event, { kind: "click" })
                    });
                }
                return;
            }
        }

        // Non-queue mode: simple click to dig
        // Prevent spam clicking if already disabled
        if (this.element.disabled) {
            return;
        }

        // Disable button to prevent spam
        this.element.disabled = true;

        // Re-enable after a short delay
        setTimeout(() => {
            this.element.disabled = false;
        }, 500);

        this.addClickFeedback();

        // Call WebSocket action
        if (window.WorkerChannel) {
            WorkerChannel.performAction("dig", {
                tile_id: tileId,
                input_proof: buildInputProof(event, { kind: "click" })
            });
        } else {
            console.warn("WorkerChannel not available, fallback needed");
        }
    }

    addClickFeedback() {
        const tileElement = this.element.querySelector('.tile');
        
        const isUndug = tileElement && tileElement.classList.contains('undug');
        
        if (tileElement && isUndug) {
            tileElement.classList.remove('tile-click-feedback');
            tileElement.offsetHeight; // Force reflow
            
            tileElement.classList.add('tile-click-feedback');
            
            setTimeout(() => {
                tileElement.classList.remove('tile-click-feedback');
            }, 300);
        }
    }

    syncQueueCounterFromDOM() {
        if (window.gameUpdatesChannel && typeof window.gameUpdatesChannel.updateQueueCounterFromDOM === 'function') {
            window.gameUpdatesChannel.updateQueueCounterFromDOM()
        }
    }
}
